import React from "react";
import blueAvatar from "../assets/blue/blue-avatar.png";
import blueSeated from "../assets/blue/blue-seated.png";
import blueAvatarHead from "../assets/blue/blue-avatar-head.png";

/**
 * BlueMascot — affichage de Blue à partir d'UNE SEULE image source.
 *
 * Les PNG sont importés en tant que modules ES : le bundler du projet
 * hôte (Webpack, Vite, CRA…) les traite comme des assets et produit
 * des URL hashées valides quel que soit le sous-chemin de déploiement.
 * Le module reste donc portable sans configuration.
 *
 * Variantes de cadrage :
 *   - "portrait"        : recadrage tête (2 oreilles) → blue-avatar-head.png
 *                         (généré par PIL depuis blue-avatar.png, box 150/20/630/500).
 *                         À utiliser dans le petit médaillon flottant.
 *   - "avatar" (défaut) : portrait tête/poitrail       → blue-avatar.png
 *   - "full"            : corps entier assis           → blue-seated.png
 *
 * États visuels (produits par CSS uniquement, cf. blue-chat.css) :
 *   - "idle"     : image telle quelle + clignement périodique (~6 s).
 *   - "thinking" : léger tilt de la tête.
 *
 * Les positions des yeux (pour l'overlay clignement) sont exprimées en
 * pourcentages, mesurées automatiquement à la génération.
 *
 * NB: la variante "portrait" (petit médaillon flottant) rend un portrait
 * statique — sans overlay clignement — pour éviter tout artefact/point
 * gris parasite sur le très petit rendu 72×72.
 */
const EYE_POS = {
  avatar:   { y: 29.9, xL: 45.4, xR: 67.9, r: 3.2 },
  full:     { y: 24.4, xL: 52.3, xR: 72.0, r: 2.2 },
};

const SRC = {
  portrait: blueAvatarHead,
  avatar:   blueAvatar,
  full:     blueSeated,
};

export const BlueMascot = React.memo(function BlueMascot({
  state = "idle",
  variant = "avatar",
  size = 96,
  title = "Blue",
  className = "",
}) {
  const eye = EYE_POS[variant] || EYE_POS.avatar;
  const src = SRC[variant] || SRC.avatar;
  // La variante "portrait" est un rendu statique propre pour le médaillon
  // flottant : pas d'overlay clignement (le petit rendu 72×72 transformait
  // les ellipses SVG en point parasite en haut à droite de la tête).
  const showBlink = variant !== "portrait";

  return (
    <div
      className={`blue-mascot blue-mascot--${state} blue-mascot--${variant} ${className}`}
      style={{ width: size, height: size }}
      data-testid="blue-mascot"
      data-state={state}
      data-variant={variant}
      aria-hidden="true"
    >
      <div className="blue-mascot__inner">
        <img
          className="blue-mascot__img"
          src={src}
          alt={title}
          draggable="false"
        />
        {showBlink && (
          <svg
            className="blue-mascot__blink"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <g>
              <ellipse cx={eye.xL} cy={eye.y} rx={eye.r} ry={eye.r * 0.55} fill="#8c98a6" />
              <ellipse cx={eye.xR} cy={eye.y} rx={eye.r} ry={eye.r * 0.55} fill="#8c98a6" />
              <path
                d={`M ${eye.xL - eye.r * 0.9} ${eye.y} Q ${eye.xL} ${eye.y + 0.6} ${eye.xL + eye.r * 0.9} ${eye.y}`}
                stroke="#3b434e"
                strokeWidth="0.35"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d={`M ${eye.xR - eye.r * 0.9} ${eye.y} Q ${eye.xR} ${eye.y + 0.6} ${eye.xR + eye.r * 0.9} ${eye.y}`}
                stroke="#3b434e"
                strokeWidth="0.35"
                fill="none"
                strokeLinecap="round"
              />
            </g>
          </svg>
        )}
      </div>
    </div>
  );
});

export default BlueMascot;
